import type { ReactNode } from "react"

import { useAuth } from "@/components/AuthProvider"
import { ArtlessDevLogo } from "@/components/ArtlessDevLogo"
import { AnalyticsNotice } from "@/components/marketing/AnalyticsNotice"
import { MarketingAnalytics } from "@/components/marketing/MarketingAnalytics"
import { ButtonLink, PlainButtonLink } from "@/components/marketing/elements/button"
import { Main } from "@/components/marketing/elements/main"
import {
  FooterCategory,
  FooterLink,
  FooterWithLinkCategories,
} from "@/components/marketing/sections/footer-with-link-categories"
import {
  NavbarLink,
  NavbarLogo,
  NavbarWithLinksActionsAndCenteredLogo,
} from "@/components/marketing/sections/navbar-with-links-actions-and-centered-logo"
import { useScrollToHash } from "@/hooks/useScrollToHash"
import { appUrl } from "@/lib/app-links"
import { primaryCtaHref, primaryCtaLabel, waitlistHref, waitlistLabel } from "@/lib/launch-config"
import { contactEmailDomain, contactEmailUser, docsUrl } from "@/lib/site-links"
import { legalOperator } from "@/lib/legal-config"
import { getUmamiConfig, isAnalyticsOptedOut, setAnalyticsOptOut } from "@/lib/umami"

function BrandLogo() {
  return (
    <>
      <ArtlessDevLogo size="lg" className="md:hidden" />
      <ArtlessDevLogo size="xl" className="hidden md:inline-flex" />
    </>
  )
}

function AnalyticsFooterControl() {
  if (!getUmamiConfig()) return null

  if (isAnalyticsOptedOut()) {
    return (
      <FooterLink
        href="#"
        onClick={(event) => {
          event.preventDefault()
          setAnalyticsOptOut(false)
          window.location.reload()
        }}
      >
        Enable analytics
      </FooterLink>
    )
  }

  return (
    <FooterLink
      href="#"
      onClick={(event) => {
        event.preventDefault()
        setAnalyticsOptOut(true)
        window.location.reload()
      }}
    >
      Analytics opt-out
    </FooterLink>
  )
}

export function MarketingLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  useScrollToHash()

  return (
    <div className="marketing min-h-screen min-w-0 max-w-full overflow-x-clip bg-background font-sans text-foreground">
      <MarketingAnalytics />
      <NavbarWithLinksActionsAndCenteredLogo
        id="navbar"
        links={
          <>
            <NavbarLink href="/#features">Features</NavbarLink>
            <NavbarLink href="/#features">How it works</NavbarLink>
            <NavbarLink href="/#agents">For AI agents</NavbarLink>
            <NavbarLink href="/#pricing">Pricing</NavbarLink>
            <NavbarLink href={docsUrl}>Docs</NavbarLink>
            <NavbarLink href="/#faqs">FAQ</NavbarLink>
          </>
        }
        logo={
          <NavbarLogo href="/">
            <BrandLogo />
          </NavbarLogo>
        }
        actions={
          user ? (
            <ButtonLink href={appUrl("/workspaces")}>Open app</ButtonLink>
          ) : (
            <>
              <PlainButtonLink href={appUrl("/sign-in")}>
                Log in
              </PlainButtonLink>
              <ButtonLink href={primaryCtaHref()}>{primaryCtaLabel()}</ButtonLink>
            </>
          )
        }
      />

      <Main>{children}</Main>

      <FooterWithLinkCategories
        id="footer"
        links={
          <>
            <FooterCategory title="Product">
              <FooterLink href="/#features">Features</FooterLink>
              <FooterLink href="/#features">How it works</FooterLink>
              <FooterLink href="/#agents">For AI agents</FooterLink>
              <FooterLink href="/#pricing">Pricing</FooterLink>
              <FooterLink href={docsUrl}>Docs</FooterLink>
              <FooterLink href="/#faqs">Q&amp;A</FooterLink>
            </FooterCategory>
            <FooterCategory title="Account">
              <FooterLink href={primaryCtaHref()}>{primaryCtaLabel()}</FooterLink>
              <FooterLink href={waitlistHref()}>{waitlistLabel()}</FooterLink>
              <FooterLink href={appUrl("/sign-in")}>Sign in</FooterLink>
            </FooterCategory>
            <FooterCategory title="Legal">
              <FooterLink
                href="#"
                onClick={(event) => {
                  event.preventDefault()
                  window.location.href = `mailto:${contactEmailUser}@${contactEmailDomain}`
                }}
              >
                Contact
              </FooterLink>
              <FooterLink href="/legal/privacy">Privacy</FooterLink>
              <FooterLink href="/legal/terms">Terms</FooterLink>
              <AnalyticsFooterControl />
            </FooterCategory>
          </>
        }
        fineprint={<p>© 2026 {legalOperator.name}</p>}
      />

      <AnalyticsNotice />
    </div>
  )
}
